import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./ProductVariantsModal.css";

const API_URL = "/api";

const initialForm = {
  name: "",
  sku: "",
  sale_price: "",
  stock: "0",
  is_active: true,
};

const parseLocalizedNumber = (value) => {
  const rawValue = String(value ?? "")
    .trim()
    .replace(/\$/g, "")
    .replace(/\s/g, "");

  if (!rawValue) {
    return null;
  }

  const hasDot = rawValue.includes(".");
  const hasComma = rawValue.includes(",");

  let normalized = rawValue;

  if (hasDot && hasComma) {
    const lastDot = rawValue.lastIndexOf(".");
    const lastComma = rawValue.lastIndexOf(",");

    if (lastComma > lastDot) {
      normalized = rawValue
        .replace(/\./g, "")
        .replace(",", ".");
    } else {
      normalized = rawValue.replace(/,/g, "");
    }
  } else if (hasDot) {
    // En Colombia: 1.900 significa mil novecientos.
    normalized = rawValue.replace(/\./g, "");
  } else if (hasComma) {
    // Permite 1,5 como decimal para cantidades.
    normalized = rawValue.replace(",", ".");
  }

  const parsedValue = Number(normalized);

  return Number.isFinite(parsedValue)
    ? parsedValue
    : null;
};

const formatPriceInput = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatStockInput = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  return new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(value));
};

const formatMoney = (value) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const formatQuantity = (value) =>
  new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(value || 0));

const getErrorMessage = (error) =>
  error?.response?.data?.detail ||
  "Ocurrió un error inesperado. Intenta nuevamente.";

function ProductVariantsModal({
  product,
  getHeaders,
  onClose,
}) {
  const [variants, setVariants] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingVariantId, setEditingVariantId] =
    useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadVariants = useCallback(async () => {
    if (!product?.id) {
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(
        `${API_URL}/products/me/${product.id}/variants`,
        {
          headers: getHeaders(),
        }
      );

      setVariants(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [getHeaders, product?.id]);

  useEffect(() => {
    loadVariants();
  }, [loadVariants]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingVariantId(null);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEdit = (variant) => {
    setEditingVariantId(variant.id);

    setForm({
      name: variant.name || "",
      sku: variant.sku || "",
      sale_price: formatPriceInput(variant.sale_price),

      stock: formatStockInput(variant.stock),
      is_active: Boolean(variant.is_active),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const name = form.name.trim();
    const sku = form.sku.trim();
    const salePrice = parseLocalizedNumber(form.sale_price);
    const stock = parseLocalizedNumber(form.stock);

    if (!name) {
      toast.error("Ingresa el nombre de la variante.");
      return;
    }

    if (!sku) {
      toast.error("Ingresa el SKU de la variante.");
      return;
    }

    if (salePrice !== null && salePrice < 0) {
      toast.error("El precio no puede ser negativo.");
      return;
    }

    if (stock === null || stock < 0) {
      toast.error("El stock debe ser cero o mayor.");
      return;
    }

    const payload = {
      name,
      sku,
      sale_price: salePrice,
      stock,
      is_active: form.is_active,
    };

    try {
      setSaving(true);

      if (editingVariantId) {
        await axios.patch(
          `${API_URL}/products/me/${product.id}/variants/${editingVariantId}`,
          payload,
          {
            headers: getHeaders(),
          }
        );

        toast.success("Variante actualizada correctamente.");
      } else {
        await axios.post(
          `${API_URL}/products/me/${product.id}/variants`,
          payload,
          {
            headers: getHeaders(),
          }
        );

        toast.success("Variante creada correctamente.");
      }

      resetForm();
      await loadVariants();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (variant) => {
    const confirmed = window.confirm(
      `¿Deseas desactivar la variante "${variant.name}"?`
    );

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/products/me/${product.id}/variants/${variant.id}`,
        {
          headers: getHeaders(),
        }
      );

      toast.success("Variante desactivada correctamente.");

      if (editingVariantId === variant.id) {
        resetForm();
      }

      await loadVariants();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (!product) {
    return null;
  }

  return (
    <div
      className="product-variants-overlay"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="product-variants-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-variants-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="product-variants-header">
          <div>
            <span className="product-variants-eyebrow">
              VARIANTES DEL PRODUCTO
            </span>

            <h2 id="product-variants-title">
              {product.name}
            </h2>

            <p>
              Crea presentaciones, tamaños, colores o sabores
              con SKU, precio y existencias independientes.
            </p>
          </div>

          <button
            type="button"
            className="product-variants-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        <div className="product-variants-content">
          <section className="product-variants-form-card">
            <div className="product-variants-section-heading">
              <h3>
                {editingVariantId
                  ? "Editar variante"
                  : "Nueva variante"}
              </h3>

              {editingVariantId && (
                <button
                  type="button"
                  className="product-variants-link-button"
                  onClick={resetForm}
                >
                  Cancelar edición
                </button>
              )}
            </div>

            <form onSubmit={handleSubmit}>
              <div className="product-variants-grid">
                <label>
                  Nombre de la variante
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Ej. Presentación 500 g"
                    maxLength={150}
                    required
                  />
                </label>

                <label>
                  SKU de la variante
                  <input
                    name="sku"
                    value={form.sku}
                    onChange={handleChange}
                    placeholder="Ej. ARV-500G"
                    maxLength={100}
                    required
                  />
                </label>

                <label>
                  Precio de venta
                  <input
                    name="sale_price"
                    value={form.sale_price}
                    onChange={handleChange}
                    type="text"
                    inputMode="decimal"
                    placeholder={`Usar precio base: ${formatMoney(
                      product.sale_price
                    )}`}
                  />
                  <small>
                    Vacío: usa el precio base del producto.
                  </small>
                </label>

                <label>
                  Stock inicial o disponible
                  <input
                    name="stock"
                    value={form.stock}
                    onChange={handleChange}
                    type="text"
                    inputMode="decimal"
                    required
                  />
                </label>
              </div>

              <label className="product-variants-checkbox">
                <input
                  name="is_active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={handleChange}
                />
                Variante activa y disponible para venta
              </label>

              <button
                type="submit"
                className="product-variants-save-button"
                disabled={saving}
              >
                {saving
                  ? "Guardando..."
                  : editingVariantId
                    ? "Guardar cambios"
                    : "Crear variante"}
              </button>
            </form>
          </section>

          <section className="product-variants-list-card">
            <div className="product-variants-section-heading">
              <h3>Variantes registradas</h3>
              <span>{variants.length}</span>
            </div>

            {loading ? (
              <p className="product-variants-empty">
                Cargando variantes...
              </p>
            ) : variants.length === 0 ? (
              <p className="product-variants-empty">
                Este producto todavía no tiene variantes.
              </p>
            ) : (
              <div className="product-variants-table-wrapper">
                <table className="product-variants-table">
                  <thead>
                    <tr>
                      <th>Variante</th>
                      <th>SKU</th>
                      <th>Precio</th>
                      <th>Stock</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>

                  <tbody>
                    {variants.map((variant) => (
                      <tr key={variant.id}>
                        <td>{variant.name}</td>
                        <td>{variant.sku}</td>
                        <td>
                          {variant.sale_price === null
                            ? "Precio base"
                            : formatMoney(variant.sale_price)}
                        </td>
                        <td>
                          {formatQuantity(variant.stock)}
                        </td>
                        <td>
                          <span
                            className={
                              variant.is_active
                                ? "product-variants-status active"
                                : "product-variants-status inactive"
                            }
                          >
                            {variant.is_active
                              ? "Activa"
                              : "Inactiva"}
                          </span>
                        </td>
                        <td className="product-variants-actions">
                          <button
                            type="button"
                            onClick={() => handleEdit(variant)}
                          >
                            Editar
                          </button>

                          {variant.is_active && (
                            <button
                              type="button"
                              className="danger"
                              onClick={() =>
                                handleDeactivate(variant)
                              }
                            >
                              Desactivar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </section>
    </div>
  );
}

export default ProductVariantsModal;